import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ApplicationSignupContainer } from "@/components/molecules/application/application-signup.container";
import { api } from "@/lib/backend";
import { type ProjectEmployeeDayCard } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useApplications } from "@/lib/query/application/use-applications-for-project";
import { useProjectSignupSlots } from "@/lib/query/availability-slot/use-project-signup-slots";
import { PROJECT_QUERY_KEY } from "@/lib/requests/project/project.key";
import { Routes } from "@/lib/router";
import { buildSignupDateGroups, openSignupDateGroups } from "@/lib/signup/date-groups";
import { getSignupSuccessMessage, getSignupTitle } from "@/lib/utils/scheduling-text";
import { logger } from "@/logger";

type DiscoverSignupSheetProps = {
  /** The tapped day, or null when the sheet is closed. */
  card: ProjectEmployeeDayCard | null;
  onDismiss: () => void;
  /** Sending the reader to the project screen — offered on every path, not only the hand-offs. */
  onOpenProject: (card: ProjectEmployeeDayCard) => void;
};

/**
 * Signing up without leaving Ontdek.
 *
 * The win of a marketplace is staying in the list you are comparing in. Tapping a row used to push
 * the project screen, which meant losing your place, your filters and the eight other rows you were
 * weighing it against.
 *
 * It asks the same questions the project screen asks — `useProjectSignupSlots` and
 * `buildSignupDateGroups`, both extracted for exactly this — so there is no second answer to "what
 * may this person sign up for" that can drift out of step with the first. Eén verschil, en het is
 * er bewust: het projectscherm toont ook de dagen waarop je je al aanmeldde (met je stand erbij),
 * en hier wordt gekozen. Vandaar `openSignupDateGroups` eromheen.
 *
 * **It hands off rather than half-doing the job.** A project with application questions needs those
 * answered before an application is valid, and asking them here would be a second copy of a form
 * that already exists. So the sheet detects them and offers the project screen instead. Same for a
 * day that turns out to have nothing open left: better to say so than to present an empty picker.
 *
 * **And the project screen stays reachable even when it does handle the job.** Signing up fast and
 * reading up first are two different needs, and the shortcut used to eat the long way: once the
 * sheet could take the application, nothing on it led to the description, the address, the
 * documents or the other days. The way out was only offered in the two cases the sheet refused —
 * exactly the cases where it is not a choice. So it is now offered on every path, secondary to the
 * signup button on the one where signing up is what you came for.
 */
export function DiscoverSignupSheet({ card, onDismiss, onOpenProject }: DiscoverSignupSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const projectId = card?.projectId;
  const day = card?.day.split("T")[0];

  // Every selection belongs to the day it was made on. Without this, dismissing the sheet and
  // opening another row would carry the previous project's slot ids into a new application.
  useEffect(() => {
    setSelectedIds([]);
  }, [projectId, day]);

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => (await api.project.get(projectId!)).data,
    enabled: !!projectId,
  });

  // Whether this project asks anything beyond "which shift". Only the count matters here; the
  // fields themselves are the project screen's job.
  const { data: customFields } = useQuery({
    queryKey: ["customFieldDefinitions", "APPLICATION", projectId],
    queryFn: () =>
      extractionWrapper(api.customFieldDefinitions.getAll("APPLICATION", { projectId })),
    enabled: !!projectId,
  });

  const slots = useProjectSignupSlots(projectId);
  const { data: applications } = useApplications(projectId);

  // `openSignupDateGroups` en niet de ruwe lijst: die houdt sinds kort ook de dagen vast waarop je
  // je al aanmeldde, zodat het projectscherm ze kan tónen. Hier wordt gekozen en niet getoond, dus
  // een dag die al van jou is heeft niets te bieden.
  const dateGroups = useMemo(
    () => openSignupDateGroups(buildSignupDateGroups(slots.items, applications?.items)),
    [slots.items, applications?.items],
  );

  // Only the tapped day. The row said what was on offer that day; opening it onto a project's whole
  // run would answer a question the reader did not ask.
  const groupsForDay = useMemo(
    () => dateGroups.filter((group) => group.date === day),
    [dateGroups, day],
  );

  const needsCustomFields = (customFields?.length ?? 0) > 0;
  const hasSomethingToPick = groupsForDay.some((group) => group.shifts.length > 0);

  // Every question has to be answered before the sheet may claim anything. Deciding on the slots
  // alone would announce "niets meer open" while the request is still out, and offer the picker to
  // a project whose application questions had not come back yet — the one case that must hand off.
  const isReady = !!card && !slots.isLoading && !!project && customFields !== undefined;

  const submit = useMutation({
    mutationFn: () =>
      api.application.createEmployeeApplication({ availabilitySlotIds: selectedIds }),
    onSuccess: async () => {
      toast.success(getSignupSuccessMessage(t, project));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["applications", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["availabilitySlots"] }),
        // The row that was just tapped now reads "aangemeld", so Ontdek itself has to catch up.
        // The key is built by `queryOptions_projects_employee_day_range`, and this prefix is what
        // matches every window and filter combination it may have cached.
        queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, "employee-day-range"] }),
      ]);
      onDismiss();
    },
    onError: (error) => {
      logger.error("Aanmelden vanaf Ontdek mislukt", { error });
      toast.error(t("common:discover.signup.failed"));
    },
  });

  return (
    <ModalSheetAutoHeight isOpen={!!card} onIonModalDidDismiss={onDismiss}>
      <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold leading-[1.4] text-txt-primary">
            {card?.projectName}
          </h2>
          <p className="text-sm text-txt-secondary">
            {getSignupTitle(t, project?.schedulingMethod)}
          </p>
        </div>

        {!isReady ? (
          <p className="py-4 text-center text-sm text-txt-secondary">{t("common:loading")}</p>
        ) : needsCustomFields || !hasSomethingToPick ? (
          <>
            <p className="text-sm text-txt-secondary">
              {needsCustomFields
                ? t("common:discover.signup.needs-questions")
                : t("common:discover.signup.nothing-open")}
            </p>
            <button
              type="button"
              onClick={() => card && onOpenProject(card)}
              className="w-full rounded-lg bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted transition-opacity active:opacity-70"
            >
              {t("common:discover.signup.open-project")}
            </button>
          </>
        ) : (
          <>
            {/* The container carries `pb-28` for the project screen's fixed footer. There is no
                fixed footer in a sheet, and its `onSubmit`/`isSubmitting` props render nothing —
                the project screen has always supplied its own button. So the sheet supplies one
                too, and cancels the space meant for the other one. */}
            <div className="-mb-24">
              <ApplicationSignupContainer
                dateGroups={groupsForDay}
                selectedShiftIds={selectedIds}
                onSelectionChange={setSelectedIds}
                schedulingMethod={project?.schedulingMethod}
                initialDate={day}
              />
            </div>

            <button
              type="button"
              disabled={selectedIds.length === 0 || submit.isPending}
              onClick={() => submit.mutate()}
              className="w-full rounded-lg bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted transition-opacity active:opacity-70 disabled:opacity-50"
            >
              {t("common:discover.signup.confirm", { count: selectedIds.length })}
            </button>

            {/* Secondary, and deliberately not a second filled button: on this path signing up is
                what the reader came for, and reading up first is the other thing they might want.
                Both have to be here; only one of them is the point. */}
            <button
              type="button"
              onClick={() => card && onOpenProject(card)}
              className="w-full py-1 text-center text-sm font-semibold text-txt-brand transition-opacity active:opacity-70"
            >
              {t("common:discover.signup.view-details")}
            </button>
          </>
        )}
      </div>
    </ModalSheetAutoHeight>
  );
}

/**
 * Where the sheet sends you when it hands off.
 *
 * The same link the row itself used before the sheet existed, `day` included — that is what makes
 * the project screen open on the signup segment for the day that was tapped rather than the first
 * one, and `fromUrl` is what makes its back button return to Ontdek.
 */
export function projectRouteForCard(card: ProjectEmployeeDayCard): string {
  return Routes.ProjectOverview(
    { id: card.projectId },
    { search: { fromUrl: Routes.Projects(), day: card.day.split("T")[0] } },
  );
}
